<template>
  <div class="container p-0" id="gift-list">
    <!-- Cabeçalho da seção -->
    <div class="gifts-section-header">
      <div>
        <h2 class="gifts-title">Lista de Presentes</h2>
        <p class="gifts-subtitle">Escolha um ou mais presentes para tornar esse momento especial</p>
      </div>
      <span class="gifts-count">{{ filteredAndSortedGifts.length }} itens</span>
    </div>

    <!-- Filtros -->
    <div class="gifts-filters">
      <div class="filter-pill">
        <i class="fas fa-arrow-up-wide-short"></i>
        <select id="sortPrice" v-model="selectedPriceSort">
          <option value="">Preço padrão</option>
          <option value="asc">Menor preço</option>
          <option value="desc">Maior preço</option>
        </select>
      </div>
      <div class="filter-pill">
        <i class="fas fa-tag"></i>
        <select id="filterCategory" v-model="selectedCategory">
          <option value="">Todas as categorias</option>
          <option v-for="category in uniqueCategories" :key="category" :value="category">{{ category }}</option>
        </select>
      </div>
    </div>

    <!-- Grid de presentes -->
    <div class="masonry-grid" ref="masonryContainer" :style="gridStyle">
      <div
        v-for="(gift, index) in filteredAndSortedGifts" :key="gift.id"
        class="masonry-item card border-0 rounded-4 shadow-sm mb-4"
      >
        <!-- Imagem + overlay -->
        <div class="gift-img-wrapper">
          <img
            v-if="gift.photo_url"
            :src="gift.photo_url"
            class="card-img-top rounded-top-4 gift-img"
            :alt="gift.name"
            @error="gift.photo_url = ''"
          />
          <div v-else class="gift-img-placeholder rounded-top-4">
            <img :src="giftDefaultImage" class="gift-img-placeholder__icon" :alt="gift.name" />
          </div>
          <!-- Category tag -->
          <span v-if="gift.category" class="gift-category-tag">{{ gift.category }}</span>

          <!-- Overlay adicionado ao carrinho -->
          <div v-if="gift.gifted" class="overlay">
            <div class="overlay-inner">
              <i class="fa-solid fa-circle-check overlay-icon"></i>
              <p class="overlay-text">No carrinho!</p>
              <button class="btn-remove" @click.stop="removerDoCarrinho(index)">Remover</button>
            </div>
          </div>
        </div>

        <!-- Corpo do card -->
        <div class="card-body px-2 pb-3 pt-2">
          <h5 class="card-title fw-semibold title-custom mb-1">{{ gift.name }}</h5>
          <p class="card-text fw-bold text-price mb-2">R$ {{ formatCurrency(gift.price) }}</p>

          <button
            class="btn btn-sm w-100"
            :class="gift.gifted ? 'btn-added-custom' : 'btn-primary-custom'"
            :disabled="gift.gifted"
            @click="adicionarAoCarrinho(gift.id)"
          >
            <span v-if="gift.gifted">
              <i class="fa-solid fa-check me-1"></i>Adicionado!
            </span>
            <span v-else>Presentear</span>
          </button>
        </div>
      </div>

      <div v-if="filteredAndSortedGifts.length === 0" class="col-12 text-center text-muted py-5">
        <i class="fas fa-box-open fa-2x mb-2 d-block"></i>
        <p>Nenhum presente encontrado com os filtros selecionados.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import { useCartStore } from '@/stores/cartStore';
import { formatCurrency } from '@/utils/currencyFormatter';
import Masonry from 'masonry-layout';
import giftDefaultImage from '@/assets/images/gift-custom.png';

const props = defineProps({
  event: {
    type: Object,
    required: true
  },
  columns: {
    type: Number,
    default: 2
  }
});

const computedColWidth = ref(null);
const itemWidthStyle = computed(() =>
  computedColWidth.value ? { width: `${computedColWidth.value}px` } : {}
);
const gridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${props.columns}, 1fr)`
}));

const cart = useCartStore();

const gifts = ref([]); // Lista original de presentes (sempre a base para filtros)
const masonryContainer = ref(null);
let msnry = null;

// Estados reativos para os filtros
const selectedPriceSort = ref(''); // 'asc', 'desc', ou '' (padrão)
const selectedCategory = ref(''); // Categoria selecionada ou '' (todas as categorias)

// --- Funções Auxiliares ---

// Computed property para obter categorias únicas para o filtro
const uniqueCategories = computed(() => {
  const categories = new Set();
  // Garante que props.event.items existe e é um array antes de iterar
  if (props.event && props.event.items && Array.isArray(props.event.items)) {
    props.event.items.forEach(item => {
      if (item.category) {
        categories.add(item.category);
      }
    });
  }
  return Array.from(categories).sort(); // Retorna array ordenado alfabeticamente
});

// Computed property para aplicar filtros e ordenação
const filteredAndSortedGifts = computed(() => {
  let result = [...gifts.value]; // Começa com uma cópia da lista original de presentes

  // 1. Filtrar por Categoria
  if (selectedCategory.value) {
    result = result.filter(gift => gift.category === selectedCategory.value);
  }

  // 2. Ordenar por Preço
  if (selectedPriceSort.value === 'asc') {
    result.sort((a, b) => a.price - b.price);
  } else if (selectedPriceSort.value === 'desc') {
    result.sort((a, b) => b.price - a.price);
  }

  return result;
});


// --- Funções de Inicialização e Lógica de Negócio ---

const initGifts = () => {
  // Inicializa a lista `gifts` com os itens do evento e seus estados
  // ATENÇÃO: Corrigido o mapeamento de imageUrl para photo_url
  if (props.event && props.event.items && Array.isArray(props.event.items)) {
    cart.hydrateEvent(props.event);
    gifts.value = props.event.items.map(item => ({
      ...item,
      photo_url: item.imageUrl, // <--- CORREÇÃO APLICADA AQUI
      gifted: false, // Estado se o item foi adicionado ao carrinho
      hover: false,
      quantity: 1
    }));
  } else {
      gifts.value = []; // Garante que gifts é um array vazio se não houver items
  }
};

const loadGiftedState = () => {
  // Atualiza o estado `gifted` dos presentes com base no carrinho
  const currentItems = cart.getItemsByEvent(props.event.id);
  gifts.value.forEach(gift => {
    gift.gifted = currentItems.some(item => item.id === gift.id);
  });
};

const adicionarAoCarrinho = (giftId) => { // Recebe o ID do presente
  const giftIndex = gifts.value.findIndex(g => g.id === giftId);
  if (giftIndex !== -1) {
    cart.addItem(props.event, gifts.value[giftIndex]);
    gifts.value[giftIndex].gifted = true;
  }
};

const removerDoCarrinho = (index) => { // Ainda funciona com index, ou você pode mudar para ID também
  // Para remover, podemos continuar usando o índice ou mudar para o ID também para consistência
  cart.removeItem(props.event.id, gifts.value[index].id);
  gifts.value[index].gifted = false;
};

// --- Funções Masonry ---

const initializeMasonry = async () => {
  // CSS Grid cuida do layout. Masonry.js desabilitado.
  if (msnry) { msnry.destroy(); msnry = null; }
};

// --- Ciclo de Vida e Watchers ---

onMounted(() => {
  initGifts();
  loadGiftedState();
  nextTick(() => { initializeMasonry(); });
});

watch(() => props.columns, () => {
  nextTick(() => { initializeMasonry(); });
});

// Observa mudanças nos filtros e nos itens do carrinho para re-renderizar
watch([selectedPriceSort, selectedCategory, () => cart.events], () => {
  loadGiftedState();
}, { deep: true });

// Watcher para reagir a mudanças na prop `event` (se o evento mudar dinamicamente)
watch(() => props.event.items, (newItems, oldItems) => {
  if (JSON.stringify(newItems) !== JSON.stringify(oldItems)) {
    initGifts();
    loadGiftedState();
  }
}, { deep: true });


onBeforeUnmount(() => {
  if (msnry) {
    msnry.destroy();
  }
});
</script>

<style scoped>
/* ── Cabeçalho da seção ──────────────────────── */
.gifts-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 0.5rem;
}

.gifts-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
}

.gifts-subtitle {
  display: none;
}

.gifts-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(6px);
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Filtros estilo pill ─────────────────────── */
.gifts-filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: border-color 0.2s, background 0.2s;
}

.filter-pill:focus-within {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(0, 0, 0, 0.4);
}

.filter-pill i {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.75rem;
  flex-shrink: 0;
}

.filter-pill select {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  padding: 0;
}

.filter-pill select option {
  color: #111;
  background: #fff;
}

/* ── Masonry grid ────────────────────────────── */
.masonry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  align-items: start;
}

.masonry-item {
  background: rgba(15, 8, 25, 0.52) !important;
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.08) !important;
  transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
}

.masonry-item:hover {
  box-shadow: 0 14px 44px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.12) !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  transform: translateY(-3px);
}

@media (max-width: 575.98px) {
  .masonry-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ── Imagem + tags ───────────────────────────── */
.gift-img-wrapper {
  position: relative;
}

.gift-img {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  border-radius: 1rem 1rem 0 0;
}

.gift-img-placeholder {
  width: 100%;
  height: 180px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
}

.gift-img-placeholder__icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.85;
}

@media (max-width: 575.98px) {
  .gift-img {
    height: 150px;
  }
}

.gift-category-tag {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background: rgba(0, 0, 0, 0.45);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255,255,255,0.1);
}

/* ── Overlay carrinho ────────────────────────── */
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(50, 53, 93, 0.82);
  border-radius: 1rem 1rem 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.overlay-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.overlay-icon {
  font-size: 1.75rem;
  color: #4ade80;
}

.overlay-text {
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  margin: 0;
}

.btn-remove {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.35);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-remove:hover {
  background: rgba(255,255,255,0.28);
}

/* ── Card body ───────────────────────────────── */
/* ── Card body ─────────────────────────── */
.card-body {
  background: rgba(255, 255, 255, 0.06) !important;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.6rem 0.75rem 0.85rem !important;
}

.title-custom {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.88rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
  min-height: calc(0.88rem * 1.35 * 2);
  text-shadow: 0 1px 4px rgba(0,0,0,0.5);
}

.text-price {
  color: #fff !important;
  font-size: 0.95rem;
  font-weight: 800 !important;
  text-shadow: 0 0 12px color-mix(in srgb, var(--event-primary, #ff50a3) 70%, transparent),
               0 1px 3px rgba(0,0,0,0.6);
  letter-spacing: 0;
}

.btn-primary-custom {
  background-color: var(--event-primary, #ff50a3);
  border-color: var(--event-primary, #ff50a3);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  border-radius: 0.5rem;
  transition: background-color 0.18s, transform 0.15s;
}

.btn-primary-custom:hover {
  background-color: color-mix(in srgb, var(--event-primary, #ff50a3) 85%, #000);
  border-color: color-mix(in srgb, var(--event-primary, #ff50a3) 85%, #000);
  transform: translateY(-1px);
}

.btn-added-custom {
  background-color: rgba(74, 222, 128, 0.12);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
  font-weight: 700;
  font-size: 0.82rem;
  border-radius: 0.5rem;
  cursor: default;
  opacity: 1;
}

.btn-added-custom:disabled {
  background-color: rgba(74, 222, 128, 0.12);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
  opacity: 1;
}

/* ── empty state ── */
.col-12.text-center.text-muted {
  color: rgba(255, 255, 255, 0.45) !important;
}
</style>